import { Appointment, DoctorsInBusiness } from "@elixir/types";
import { useEffect, useMemo } from "react";
import { DoctorFilterStripUi, SearchBarComponentUi } from "ui";
import useAllQueuesQuery from "../../../apis/tryst/get-all-queues";
import useQueueStore from "../../../stores/queue-store";
import { useMyAccessProfileV2 } from "../../../apis/tryst/get-my-access-profile";
import DoctorsList from "./doctor-list";
import useGetQueueConfig from "../../../apis/tryst/get-queue-config";

const DoctorFilter = () => {
  const { data: queueConfigData } = useGetQueueConfig();
  const { workflow_views } = queueConfigData?.data || {};
  const { data: queueData } = useAllQueuesQuery();

  const queueApts = useMemo(() => {
    const apts: Set<Appointment> = new Set();
    const viewsToAggregate =
      workflow_views
        ?.filter((view) => view.includeInAgg)
        ?.map((view) => view.viewId) || [];

    viewsToAggregate.forEach((view) => {
      if (view) {
        const viewApts = queueData?.[view] || [];
        viewApts.forEach((apt) => {
          apts.add(apt);
        });
      }
    });

    return Array.from(apts);
  }, [queueData, workflow_views]);

  const setSelectedDoctor = useQueueStore((state) => state.setSelectedDoctor);
  const selectedDoctor = useQueueStore((state) => state.selectedDoctor);
  const clinicId = useQueueStore((state) => state.clinicId);
  const isAllDoctorsHidden = useQueueStore((state) => state.isAllDoctorsHidden);

  const { data: myAccessProfile } = useMyAccessProfileV2({ clinicId });

  useEffect(() => {
    if (selectedDoctor) {
      document
        .getElementById(`doctor-chip-${selectedDoctor.name}`)
        ?.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "center",
        });
    }
  }, [selectedDoctor]);


  useEffect(() => {
    if (!myAccessProfile?.hasAccessToMultipleDoctors) {
      return;
    }
    if (!selectedDoctor) {
      if (!isAllDoctorsHidden()) {
        setSelectedDoctor(undefined);
      } else {
        setSelectedDoctor(myAccessProfile?.doctors?.[0]);
      }
    } else {
      if (clinicId !== "all") {
        if (!myAccessProfile?.allDocs
          .find((doc) => doc.oid === selectedDoctor?.oid)
          ?.clinics.includes(clinicId)) {
          if (!isAllDoctorsHidden()) {
            setSelectedDoctor(undefined);
          } else {
            setSelectedDoctor(myAccessProfile?.doctors?.[0]);
          }

        }
      }
    }
  }, [clinicId, myAccessProfile?.hasAccessToMultipleDoctors, selectedDoctor, isAllDoctorsHidden()]);

  if (!myAccessProfile?.hasAccessToMultipleDoctors) {
    return null;
  }

  return (
    <div className="dr-space-x-4 dr-z-1 dr-pb-16 dr-flex dr-flex-row dr-overflow-x-scroll dr-relative">
      {!isAllDoctorsHidden() && <DoctorFilterStripUi
        aptCount={queueApts?.length || 0}
        doctorStatusList={[]}
        isSelected={!selectedDoctor}
        onClick={() => setSelectedDoctor(undefined)}
      />}
      <DoctorsList
        onSelectDoctorClick={setSelectedDoctor}
        showDoctorAptCount={!isAllDoctorsHidden()}
        doctorsList={myAccessProfile?.doctors || []}
      />
    </div>
  );
};

export default DoctorFilter;

export const DoctorSearch = ({
  inputValue,
  setInputValue,
  list = [],
  setSelectedDoctor,
  selectedDoctor,
}: {
  list: DoctorsInBusiness[];
  inputValue: string;
  selectedDoctor: DoctorsInBusiness | undefined;
  setInputValue: React.Dispatch<React.SetStateAction<string>>;
  setSelectedDoctor: (doctor: DoctorsInBusiness | undefined) => void;
}) => {
  return (
    <div className="dr-w-250 dr-overflow-hidden dr-rounded-b-8">
      <SearchBarComponentUi
        className="dr-rounded-8 dr-border-transparent"
        placeholder="Search Doctor"
        onChange={(e) => {
          setInputValue(e.target.value);
        }}
        inputValue={inputValue}
      />
      <div className="dr-h-1 dr-w-full dr-bg-darwin-neutral-200"></div>
      <div className="dr-max-h-500 dr-overflow-y-scroll">
        {list.map((doctor) => (
          <DoctorTile
            isSelected={selectedDoctor?.oid === doctor.oid}
            onClick={() => setSelectedDoctor(doctor)}
            key={doctor.oid}
            doctor={doctor}
          />
        ))}
      </div>
    </div>
  );
};

const DoctorTile = ({
  isSelected = false,
  doctor,
  onClick,
}: {
  isSelected: boolean;
  doctor: DoctorsInBusiness;
  onClick: () => void;
}) => {
  return (
    <button
      onClick={onClick}
      className={`dr-cursor-pointer dr-w-full dr-flex dr-items-center dr-space-x-8 dr-py-8 dr-px-16 ${isSelected
          ? "dr-bg-darwin-primary-bg-light"
          : "hover:dr-bg-darwin-neutral-50"
        }`}
    >
      <img
        className="dr-w-36 dr-h-36 dr-rounded-full"
        src={doctor.pic}
        alt={doctor.name}
      />
      <div className="dr-line-clamp-1">{doctor.name}</div>
    </button>
  );
};
